import { defineStore } from 'pinia';

/**
 * Auth store — owner of the session-level identity.
 *
 * Server is the source of truth: the user is initialised by calling
 * `loadSession()` after mount; the backend returns either a 200 with the
 * full user shape or 401 (route guard handles redirect to /login).
 *
 * The `user.tenantScope.tenants` array is what drives:
 *   - TenantPicker visibility (length > 1).
 *   - The default active tenant (first entry) for the tenant store.
 *   - The role-gated nav (admin-only links if role === 'platform_admin').
 */
export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    loading: false,
    error: null,
    // Story 5.4 — deployment fingerprint. `smtpEnabled` driving:
    //   - "Forgot password?" link visibility en LoginView (AC1, AC3)
    //   - Router guard que redirige `/reset-password*` cuando es false (AC2)
    // null = no fetched aún; el guard se queda en stand-by hasta que se
    // resuelva (boot-time fetchConfig).
    config: { smtpEnabled: null }
  }),

  getters: {
    isAuthenticated: (state) => state.user !== null,
    role: (state) => state.user?.role ?? null,
    isAdmin: (state) => state.user?.role === 'platform_admin',
    isOperator: (state) => state.user?.role === 'tenant_operator',
    // El backend (login route) expande `tenant_scope` a objetos
    // {app_id, name} vía resolveTenantScope. Igual blindamos contra
    // una shape inesperada (string sentinela 'all', null) para que un
    // `.find()` en un consumidor nunca tumbe el render — devolvemos
    // siempre un array.
    tenants: (state) => {
      const t = state.user?.tenantScope?.tenants;
      return Array.isArray(t) ? t : [];
    },
    isMultiTenant: (state) => {
      const t = state.user?.tenantScope?.tenants;
      return Array.isArray(t) && t.length > 1;
    },
    smtpEnabled: (state) => state.config.smtpEnabled === true
  },

  actions: {
    setUser(user) {
      this.user = user;
      this.loading = false;
      this.error = null;
    },

    clear() {
      this.user = null;
      this.error = null;
      this.loading = false;
    },

    async loadSession(api) {
      this.loading = true;
      try {
        const resp = await api.get('/auth/session');
        this.setUser(resp?.user ?? null);
        return this.user;
      } catch (e) {
        this.user = null;
        this.error = e;
        return null;
      } finally {
        this.loading = false;
      }
    },

    // Story 5.4 — fetch deployment config on boot. Idempotente —
    // segundas llamadas devuelven la copia cacheada sin re-fetch.
    async fetchConfig(fetcher) {
      if (this.config.smtpEnabled !== null) return this.config;
      try {
        const resp = await fetcher();
        this.config = { smtpEnabled: !!resp?.smtpEnabled };
      } catch {
        // Si /auth/config falla (backend down), asumimos smtpEnabled:false
        // como default seguro — el frontend esconderá la "Forgot password?"
        // link y redirigirá las reset rutas a Unavailable.
        this.config = { smtpEnabled: false };
      }
      return this.config;
    },

    setConfig(config) {
      this.config = { smtpEnabled: !!config?.smtpEnabled };
    }
  }
});
